import type { TFunction, I$W, Experiments, ISiteApis } from '@wix/yoshi-flow-editor';
import { context } from '../../context/RootContext';
import { LABELS_LIMIT } from '../../api/consts';
import {
  DISHES_WIDGET_COMPONENT_IDS,
  UNAVAILABLE_DISHES_COMPONENT_IDS,
} from '../../appConsts/blocksIds';
import { isItemValidInCart, getAltText, buildImgSrc } from './utils';
import { DISHES_WIDGET_STATES, DISH_STATES } from '../../types/businessTypes';
import type { ItemData } from '../../types/item';
import type { DishesWidgetState, DispatchInfo, DispatchType } from '../../types/businessTypes';
import { getVariantWithMinimalPrice } from '../../utils/priceVariantsUtils';
import { dispatchState } from 'root/states/DispatchState';
import { getAreNotEnoughModifiersOfMandatoryModifierGroupInStock } from 'root/utils/itemModalControllerUtils';
import { menusState } from 'root/states/MenusState';
import {
  onItemModalClosed,
  openItemModal,
  openItemOrDispatchModal,
  openDispatchModal,
} from 'root/utils/utils';
import { when, autorun } from 'mobx';
import { navigationMenuState } from 'root/states/NavigationMenuState';
import { cartState } from 'root/states/cartState';
import { itemState } from 'root/states/itemsState';
import { SPECS } from 'root/appConsts/experiments';
import { ADD_TO_CART_ERRORS } from 'root/services/cartService';
import { sectionsState } from 'root/states/SectionsState';

const DISH_STATE_TO_IDS = {
  [DISH_STATES.regular]: DISHES_WIDGET_COMPONENT_IDS,
  [DISH_STATES.unavailable]: UNAVAILABLE_DISHES_COMPONENT_IDS,
};

const SLIDING_WINDOW_SIZE = 10;

const getDishesState = (
  itemIds: string[],
  isTruncated: boolean,
  size: number,
  isFetching: boolean
): DishesWidgetState => {
  let dishState =
    itemIds.length > 0 ? DISHES_WIDGET_STATES.dishes : DISHES_WIDGET_STATES.dishesEmpty;
  if ((isTruncated && size === 0) || (isFetching && itemIds.length !== 0)) {
    dishState = DISHES_WIDGET_STATES.loading;
  }
  return dishState;
};

export class DishesController {
  private didInitItemState = {} as Record<string, Record<keyof typeof DISH_STATES, boolean>>;
  private timezone: string;
  constructor(
    private $w: I$W,
    private t: TFunction,
    private isMobile: boolean,
    private experiments: Experiments,
    _timezone: string,
    private locale: string,
    private site: ISiteApis,
    private isMemberLoggedIn?: boolean
  ) {
    this.timezone = dispatchState.timezone || _timezone;
  }

  private reportInvalidItem(itemData: ItemData, menuId: string, sectionId: string) {
    const { name, id } = itemData;
    context.biReporterService?.reportOloGenericDebugBiEvent({
      subjectType: 'failed to render an item',
      value: {
        itemName: name,
        itemId: id,
        menuId,
        sectionId,
        operationId: dispatchState.currentOperationId,
        operationGroup: dispatchState.currentOperation?.operationGroupId,
      },
    });
  }

  private isValidItem(itemData: ItemData): boolean {
    // Check if item has valid ID
    if (!itemData.id || !itemData._id) {
      return false;
    }

    // Check if item has valid name
    if (!itemData.name || itemData.name.trim() === '') {
      return false;
    }
    // Check if item has valid price info
    if (!itemData.price || itemData.price.amount == null || itemData.price.amount < 0) {
      return false;
    }
    // Check if priceVariants exists and has valid variants
    if (itemData.priceVariants && !itemData.priceVariants.variants) {
      return false;
    }
    // Check if priceVariants has valid variants when it exists
    if (itemData.priceVariants?.variants && itemData.priceVariants.variants.length === 0) {
      return false;
    }

    // Check if any price variant has invalid price info
    if (itemData.priceVariants?.variants) {
      const hasInvalidVariant = itemData.priceVariants.variants.some(
        (variant) =>
          !variant.priceInfo ||
          variant.priceInfo.price == null ||
          Number(variant.priceInfo.price) < 0
      );
      if (hasInvalidVariant) {
        return false;
      }
    }

    return true;
  }

  setItems(items: string[], size: number, menuId: string, sectionId: string) {
    const itemIds = items.filter((id) => itemState.itemMap.has(id));
    const allItemsData = itemIds.slice(0, size).map((_id) => ({
      _id,
      ...itemState.itemMap.get(_id),
    }));

    // Filter out broken items
    const validItems = allItemsData.filter((item) => {
      const isValid = this.isValidItem(item as ItemData);
      if (!isValid) {
        this.reportInvalidItem(item as ItemData, menuId, sectionId);
      }
      return isValid;
    });

    this.$w(DISHES_WIDGET_COMPONENT_IDS.repeaterItems).data = validItems;
  }

  initItemStateInitializationMap(itemIds: string[]) {
    this.didInitItemState = itemIds.reduce((acc, id) => {
      acc[id] = {
        [DISH_STATES.regular]: false,
        [DISH_STATES.unavailable]: false,
      };
      return acc;
    }, {} as Record<string, Record<keyof typeof DISH_STATES, boolean>>);
  }

  async onDispatchModalSave({
    dispatchType,
    dispatchInfo,
    operationId,
    isLocationChanged,
  }: {
    dispatchType: DispatchType;
    dispatchInfo: DispatchInfo;
    operationId: string;
    isLocationChanged: boolean;
  }) {
    const isCurrentOperationChanged = dispatchState.operationId !== operationId;
    isCurrentOperationChanged && dispatchState.setCurrentOperationId(operationId);
    dispatchState.setIsLocationSelected(true);
    dispatchState.update(dispatchType, dispatchInfo);
    if (isLocationChanged) {
      await context.CartService?.clearCart();
    }
    context.CartService?.setShippingDetails(dispatchState.getShippingDetails());
    await menusState.updateAvailabilityStatus(
      dispatchState.currentOperationId,
      dispatchInfo,
      dispatchType
    );
  }

  openDispatchModalAfterItemModal = async ({
    dishModalResponse,
    ghostMode,
    shouldOpenItemModalBeforeDispatch,
    itemData,
    menuId,
    sectionId,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dishModalResponse: any;
    ghostMode: boolean;
    shouldOpenItemModalBeforeDispatch: boolean;
    itemData: ItemData;
    menuId: string;
    sectionId: string;
  }) => {
    if (dishModalResponse?.additionaldata) {
      if (shouldOpenItemModalBeforeDispatch && !dispatchState.dispatchInfo.address && !ghostMode) {
        openDispatchModal({
          dispatchState: dispatchState.state,
          context,
          onSave: async ({ dispatchType, dispatchInfo, operationId, isLocationChanged }) => {
            const isCurrentOperationChanged = dispatchState.operationId !== operationId;
            this.onDispatchModalSave({
              dispatchType,
              dispatchInfo,
              operationId,
              isLocationChanged,
            });
            let addItemToCartResponse;
            if (!isCurrentOperationChanged) {
              const { additionaldata } = dishModalResponse ?? {};
              addItemToCartResponse = context.CartService?.addItemToCart(
                additionaldata.cartLineItem,
                itemData?.id ?? undefined,
                dispatchState.currentOperation?.locationId
              );
            } else {
              addItemToCartResponse = Promise.resolve({
                error: ADD_TO_CART_ERRORS.MIXED_CART_LOCATION,
              });
            }
            dishModalResponse.data = addItemToCartResponse;
            await onItemModalClosed(
              itemData,
              menuId,
              sectionId,
              context,
              this.t,
              dishModalResponse,
              this.site
            );
          },
        });
      } else {
        await onItemModalClosed(
          itemData,
          menuId,
          sectionId,
          context,
          this.t,
          dishModalResponse,
          this.site
        );
      }
    }
  };

  async init(
    isEditor: boolean,
    menuId: string,
    sectionId: string,
    isTruncated: boolean,
    size: number,
    itemIds: string[]
  ) {
    const menuState = menusState.getMenu(menuId);
    const sectionState = sectionsState.getSection(sectionId);
    const dishesWidgetState = getDishesState(itemIds, isTruncated, size, itemState.isLoading);
    this.switchState(dishesWidgetState);
    this.initItemStateInitializationMap(itemIds);

    if (isEditor) {
      this.restoreItemCounterInEditor(menuId, sectionId);
    }

    const onItemInteraction = async (itemData: ItemData, amount?: string | number) => {
      const isItemInStock = !!itemData.orderSettings?.inStock;
      context.biReporterService?.reportOloLiveSiteClickOnItemBiEvent({
        itemName: itemData.name,
        itemId: itemData.id,
        menuId,
        sectionId,
        minItemPrice: Number(amount || 0) * 100, // decimal not supported
        operationId: dispatchState.currentOperationId,
        isMenuItemAvailable: menusState.getMenu(menuId)?.isAvailable ?? true,
        isItemInStock,
        operationGroup: dispatchState.currentOperation?.operationGroupId,
        locationId: dispatchState.currentOperation?.locationId,
        isDefaultLocation: dispatchState.currentOperation?.locationDetails?.default,
      });

      const enableMultiLocation = this.experiments.enabled(SPECS.enableMultiLocation);
      const shouldOpenItemModalBeforeDispatch = this.experiments.enabled(
        SPECS.openItemModalBeforeDispatch
      );

      const isLocationSelected = !(
        dispatchState.isMultiLocation && !dispatchState.isLocationSelected
      );
      const ghostMode = enableMultiLocation && dispatchState.isMultiLocation && !isLocationSelected;

      const dishModalResponse = await openItemOrDispatchModal({
        enableMultiLocation,
        shouldOpenItemModalBeforeDispatch,
        isMemberLoggedIn: this.isMemberLoggedIn,
        itemData,
        menuId,
        sectionId,
        context,
        locale: this.locale,
        timezone: this.timezone,
        t: this.t,
        onSave: async ({ dispatchType, dispatchInfo, operationId, isLocationChanged }) => {
          const isCurrentOperationChanged = dispatchState.operationId !== operationId;
          const dispatchModalSavePromise = this.onDispatchModalSave({
            dispatchType,
            dispatchInfo,
            operationId,
            isLocationChanged,
          });

          if (!isCurrentOperationChanged) {
            await dispatchModalSavePromise;
            const SHOULD_ADD_ITEM_TO_CART = true;
            openItemModal(
              context,
              itemData,
              menuId,
              sectionId,
              SHOULD_ADD_ITEM_TO_CART,
              this.locale,
              this.timezone,
              this.t,
              this.onDispatchModalSave
            );
          }
        },
      });

      this.openDispatchModalAfterItemModal({
        dishModalResponse,
        ghostMode,
        shouldOpenItemModalBeforeDispatch,
        itemData,
        menuId,
        sectionId,
      });
    };

    this.$w(DISHES_WIDGET_COMPONENT_IDS.repeaterItems).onItemReady(
      ($item: I$W, itemData: ItemData, itemIndex: number) => {
        const areNotEnoughModifiersOfMandatoryModifierGroupInStock =
          getAreNotEnoughModifiersOfMandatoryModifierGroupInStock(itemData.modifierGroups);
        const isInStockItem =
          (itemData.orderSettings?.inStock &&
            !areNotEnoughModifiersOfMandatoryModifierGroupInStock) ??
          true;

        const amount = itemData.priceVariants
          ? getVariantWithMinimalPrice(itemData.priceVariants.variants || [])?.priceInfo.price
          : itemData.price.amount;

        $item(DISHES_WIDGET_COMPONENT_IDS.itemContainer).onClick(() =>
          onItemInteraction(itemData, amount)
        );

        const enableSectionItemAvailability = this.experiments.enabled(
          SPECS.sectionItemAvailability
        );

        if (enableSectionItemAvailability) {
          const singleItemState = itemState.getSingleItemState(itemData._id);

          const itemAvailability = itemData.orderSettings?.availability;

          if (itemAvailability) {
            singleItemState?.updateItemAvailability(itemAvailability);
          }

          autorun(() => {
            singleItemState?.initializeAvailability(
              dispatchState.dispatchInfo.selectedTimeSlot,
              dispatchState.timezone || this.timezone,
              dispatchState.currentOperation?.businessDaysAheadHandlingOptions
            );
          });
        }

        const dishMultiState = $item(DISHES_WIDGET_COMPONENT_IDS.dishStateMultiStateBox);
        autorun(() => {
          const singleItemState = itemState.getSingleItemState(itemData._id);
          const effectiveAvailability = singleItemState?.getEffectiveAvailability({
            menuState,
            sectionState,
            enableSectionItemAvailability,
          });

          const formattedPrice = context.priceFormatter(Number(amount));
          const dishState =
            effectiveAvailability?.isUnavailable ||
            effectiveAvailability?.isAvailableForFutureOrder ||
            !isInStockItem
              ? DISH_STATES.unavailable
              : DISH_STATES.regular;
          const formattedPriceText = this.getItemPriceText(
            isInStockItem,
            !!effectiveAvailability?.isUnavailable,
            !!effectiveAvailability?.isAvailableForFutureOrder,
            formattedPrice
          );

          dishMultiState.changeState(dishState);
          $item(DISH_STATE_TO_IDS[dishState].itemPrice).text = formattedPriceText;

          if (!this.didInitItemState[itemData.id][dishState]) {
            this.initItem(
              DISH_STATE_TO_IDS[dishState],
              $item,
              itemData,
              isEditor,
              menuId,
              sectionId,
              Number(amount),
              onItemInteraction
            );
            this.didInitItemState[itemData.id][dishState] = true;
          }
        });

        const itemIdsBySize = itemIds.filter((id) => itemState.itemMap.has(id)).slice(0, size);

        const isTenthToLastItem =
          itemData.id === itemIdsBySize[Math.max(0, size - SLIDING_WINDOW_SIZE)];

        const shouldLoadMoreOnScroll = isTruncated && isTenthToLastItem && size < itemIds.length;

        if (shouldLoadMoreOnScroll) {
          dishMultiState.onViewportEnter(() => {
            if (!navigationMenuState.isNavigating && !itemState.isLoading) {
              this.setItems(itemIds, itemIds.length, menuId, sectionId);
            }
          });
        }
      }
    );

    when(
      () => !itemState.isLoading,
      async () => {
        this.setItems(itemIds, size, menuId, sectionId);
        const dishesState = getDishesState(itemIds, isTruncated, size, false);
        this.switchState(dishesState);

        if (dishesState === DISHES_WIDGET_STATES.loading || itemIds.length !== size) {
          when(
            () => {
              const { selectedSectionId, sectionScrollList, isNavigating } = navigationMenuState;
              const prevSectionId = sectionScrollList[sectionId]?.prevSectionId;
              const isInViewPort = sectionScrollList[sectionId]?.isInViewPort;

              const isPrevSectionInViewport =
                prevSectionId && sectionScrollList[prevSectionId]?.isInViewPort;

              const isNavigatingToLowerSection =
                !this.isMobile &&
                isNavigating &&
                sectionScrollList[selectedSectionId].index > sectionScrollList[sectionId].index;

              const shouldLoadMoreItems =
                (isPrevSectionInViewport || isInViewPort) &&
                (!this.isMobile || !isNavigating || selectedSectionId === sectionId);

              return shouldLoadMoreItems || isNavigatingToLowerSection;
            },
            () => {
              this.setItems(itemIds, itemIds.length, menuId, sectionId);
              this.switchState(getDishesState(itemIds, isTruncated, itemIds.length, false));
            }
          );
        }
      }
    );
  }

  initItem(
    componentIds: typeof DISHES_WIDGET_COMPONENT_IDS | typeof UNAVAILABLE_DISHES_COMPONENT_IDS,
    // @ts-expect-error
    $item,
    itemData: ItemData,
    isEditor: boolean,
    menuId: string,
    sectionId: string,
    price: number,
    onItemInteraction: (itemData: ItemData, amount?: string | number) => void
  ) {
    const title = $item(componentIds.itemTitle);
    title.text = itemData?.name || '';
    if (!itemData?.name) {
      title.hide();
      this.isMobile && title.collapse();
    } else {
      title.collapsed && title.expand();
      title.hidden && title.show();
    }

    if (!isEditor) {
      const titleWrapper = $item(componentIds.itemTitleWrapper);
      if (!itemData.name) {
        titleWrapper.hide();
        this.isMobile && titleWrapper.collapse();
      } else {
        titleWrapper.collapsed && titleWrapper.expand();
        titleWrapper.hidden && titleWrapper.show();
      }
      titleWrapper.onKeyPress((e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onItemInteraction(itemData, price);
        }
      });

      if (titleWrapper.accessibility) {
        titleWrapper.accessibility.ariaAttributes.haspopup = 'true';
        titleWrapper.accessibility.role = 'button';
        titleWrapper.accessibility.tabIndex = 0;
      }
    }

    const description = $item(componentIds.itemDescription);
    description.text = itemData?.description || '';
    if (!itemData?.description) {
      description.hide();
      this.isMobile && description.collapse();
    } else {
      description.collapsed && description.expand();
      description.hidden && description.show();
    }

    const image = $item(componentIds.itemImage);

    if (itemData.image?.url) {
      image.src = (itemData.image && buildImgSrc(itemData.image)) ?? '';
      image.alt = getAltText({ itemName: itemData.image.altText ?? itemData.name, t: this.t });
    } else {
      image.collapse();
    }

    const hasLabels = itemData.labels?.length > 0;

    const isFeaturedItemExperimentEnabled = this.experiments.enabled(SPECS.featuredItemDesign);
    const featured = $item(componentIds.featured);
    const featuredLabelsContainer = $item(componentIds.featuredLabelsContainer);

    const isFeaturedItem = isFeaturedItemExperimentEnabled && itemData.featured;

    if (hasLabels || isFeaturedItem) {
      featuredLabelsContainer.collapsed && featuredLabelsContainer.expand();
      featuredLabelsContainer.hidden && featuredLabelsContainer.show();
    } else {
      featuredLabelsContainer.collapse?.();
      featuredLabelsContainer.hide?.();
    }

    if (isFeaturedItem) {
      if (featured.accessibility) {
        featured.accessibility.ariaAttributes.label = this.t(
          'menu_olo.item.featured-icon-alt-text'
        );
      }
      featured.collapsed && featured.expand();
      featured.hidden && featured.show();
      featured.deleted && featured.restore();
    } else {
      if (isEditor) {
        !featured.deleted && featured.delete();
      } else {
        featured.collapse?.();
        featured.hide?.();
      }
    }

    const labelContainer = $item(componentIds.labelContainer);
    if (hasLabels) {
      labelContainer.collapsed && labelContainer.expand();
      labelContainer.hidden && labelContainer.show();
      this.initLabels(itemData, $item, componentIds.label, componentIds.additionalLabelsCounter);
    } else {
      this.isMobile && labelContainer.collapse();
      labelContainer.hide();
    }

    const cartLineItemsKey = `${menuId}_${sectionId}_${itemData.id}`;
    autorun(() => {
      const cartItems = cartState.cartLineItems.get(cartLineItemsKey);
      const hasItemInCart = isItemValidInCart(cartItems);
      const itemCounter = $item(componentIds.itemCounter);
      const itemCounterValue = $item(componentIds.itemCounterValue);
      if (hasItemInCart) {
        itemCounter.collapsed && itemCounter.expand();
        itemCounter.hidden && itemCounter.show();
      } else {
        itemCounter.collapse();
        itemCounter.hide();
      }
      itemCounterValue.text = (
        cartItems?.reduce(
          (previousValue, currentValue) => previousValue + (currentValue?.quantity || 0),
          0
        ) || 0
      ).toString();
    });
  }

  initLabels(
    itemData: ItemData,
    $item: I$W,
    getLabelElement: (idx: number) => string,
    additionalLabelsCounterId: string
  ) {
    for (let i = 0; i < LABELS_LIMIT; i++) {
      const currentLabel = itemData.labels?.[i];
      const currentLabelElement = $item(getLabelElement(i + 1));

      if (currentLabel && currentLabel.icon?.url) {
        currentLabelElement.collapsed && currentLabelElement.expand();
        currentLabelElement.src = currentLabel.icon?.url;
        currentLabelElement.alt = currentLabel.name;
        if (currentLabelElement.accessibility) {
          currentLabelElement.accessibility.ariaAttributes.label = currentLabel.name;
        }
      } else {
        currentLabelElement.collapse();
      }
    }

    const numOfAdditionalLabels = itemData.labels.length - LABELS_LIMIT;
    const showLabelCounter = itemData.labels.length > LABELS_LIMIT;
    const PLUS_SIGN = '+';
    const additionalLabelsCounterIdElement = $item(additionalLabelsCounterId);
    additionalLabelsCounterIdElement.text = showLabelCounter
      ? `${PLUS_SIGN}${numOfAdditionalLabels}`
      : '';
    if (showLabelCounter) {
      additionalLabelsCounterIdElement.collapsed && additionalLabelsCounterIdElement.expand();
      additionalLabelsCounterIdElement.hidden && additionalLabelsCounterIdElement.show();
    } else {
      additionalLabelsCounterIdElement.collapse();
      additionalLabelsCounterIdElement.hide();
    }
  }

  switchState(dishState: DishesWidgetState) {
    const multiStateBox = this.$w(DISHES_WIDGET_COMPONENT_IDS.dishesWidgetMultiStateBox);
    multiStateBox.changeState(dishState);
  }

  getItemPriceText(
    isInStockItem: boolean,
    isItemUnavailable: boolean,
    isItemAvailableForFutureOrder: boolean,
    formattedPrice: string
  ) {
    let itemPriceText;
    if (isItemUnavailable) {
      itemPriceText = this.t('menu_olo.itemUnavailable.itemPrice', {
        price: formattedPrice,
      });
    } else if (isItemAvailableForFutureOrder) {
      itemPriceText = this.t('menu_olo.futureOrder.itemPrice', {
        price: formattedPrice,
      });
    } else if (!isInStockItem) {
      itemPriceText = this.t('menu_olo.OutOfStock.itemPrice', {
        price: formattedPrice,
      });
    } else {
      itemPriceText = formattedPrice;
    }

    return itemPriceText;
  }

  deleteItemCounterInEditor(menuId: string) {
    this.$w(DISHES_WIDGET_COMPONENT_IDS.repeaterItems).forEachItem(
      ($item: I$W, itemData: ItemData) => {
        const isInStockItem = itemData.orderSettings?.inStock ?? true;
        const isMenuOfItemAvailable = menusState.getMenu(menuId)?.isAvailable ?? true;
        const dishesWidgetIds =
          isInStockItem && isMenuOfItemAvailable
            ? DISHES_WIDGET_COMPONENT_IDS
            : UNAVAILABLE_DISHES_COMPONENT_IDS;
        $item(dishesWidgetIds.itemCounter).delete();
      }
    );
  }

  restoreItemCounterInEditor(menuId: string, sectionId: string) {
    this.$w(DISHES_WIDGET_COMPONENT_IDS.repeaterItems).forEachItem(
      ($item: I$W, itemData: ItemData) => {
        const isInStockItem = itemData.orderSettings?.inStock ?? true;
        const isMenuOfItemAvailable = menusState.getMenu(menuId)?.isAvailable ?? true;
        const dishesWidgetIds =
          isInStockItem && isMenuOfItemAvailable
            ? DISHES_WIDGET_COMPONENT_IDS
            : UNAVAILABLE_DISHES_COMPONENT_IDS;

        const shouldRestoreBadge = this.hasItemsInCart(menuId, sectionId, itemData.id);
        shouldRestoreBadge && $item(dishesWidgetIds.itemCounter).restore();
      }
    );
  }

  hasItemsInCart(menuId: string, sectionId: string, itemId: string) {
    const cartLineItemsKey = `${menuId}_${sectionId}_${itemId}`;
    const cartItems = cartState.cartLineItems.get(cartLineItemsKey);

    const hasItemInCart = isItemValidInCart(cartItems);
    return hasItemInCart;
  }

  setEmptyState() {
    this.$w(DISHES_WIDGET_COMPONENT_IDS.dishesWidgetMultiStateBox).changeState(
      DISHES_WIDGET_STATES.dishesEmpty
    );
    this.$w(DISHES_WIDGET_COMPONENT_IDS.dishEmptyStateTitle).text = this.t(
      'menu_olo.emptyState.title'
    );
    this.$w(DISHES_WIDGET_COMPONENT_IDS.dishEmptyStateSubtitle).text = this.t(
      'menu_olo.emptyState.subTitle'
    );
  }
}
